import React, { useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
  Chip,
  Pagination,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Spinner,
  Switch,
} from "@heroui/react";
import { Pencil, Trash2, Eye, Lock, UserPlus } from "lucide-react";

import { User, Role } from "@/types/users";
import { usersService } from "@/services/usersService";
import { formatDate } from "@/utils/formatters";

interface UsersTableProps {
  users: User[];
  roles: Role[];
  loading: boolean;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onUserCreated: () => void;
  onUserDeleted: (userId: number) => Promise<void>;
}

export default function UsersTable({
  users,
  roles,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onUserCreated,
  onUserDeleted,
}: UsersTableProps) {
  // View/Edit user modal
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    role_id: 0,
    is_active: true,
  });
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");

  // Create user modal
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const [newUserData, setNewUserData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    role_id: 0,
    password: "",
    confirm_password: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Password change modal
  const {
    isOpen: isPasswordOpen,
    onOpen: onPasswordOpen,
    onClose: onPasswordClose,
  } = useDisclosure();
  const [passwordData, setPasswordData] = useState({
    user_id: 0,
    password: "",
    confirm_password: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email || "",
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || "",
      role_id: user.role_id,
      is_active: user.is_active,
    });
    setViewMode(true);
    setError("");
    onEditOpen();
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email || "",
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || "",
      role_id: user.role_id,
      is_active: user.is_active,
    });
    setViewMode(false);
    setError("");
    onEditOpen();
  };

  const handleCreateUser = () => {
    setNewUserData({
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      phone: "",
      role_id: roles.length > 0 ? roles[0].id : 0,
      password: "",
      confirm_password: "",
    });
    setCreateError("");
    onCreateOpen();
  };

  const handlePasswordChange = (userId: number) => {
    setPasswordData({
      user_id: userId,
      password: "",
      confirm_password: "",
    });
    setPasswordError("");
    onPasswordOpen();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;

    if (viewMode) return;

    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleNewUserInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setNewUserData({
      ...newUserData,
      [name]: value,
    });
  };

  const handlePasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;

    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const handleStatusChange = (checked: boolean) => {
    if (viewMode) return;

    setFormData({
      ...formData,
      is_active: checked,
    });
  };

  const validateUserForm = (isNew = false) => {
    if (!formData.username.trim() && !isNew) {
      setError("Username is required");

      return false;
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      setError("Invalid email format");

      return false;
    }

    if (!formData.first_name.trim() && !isNew) {
      setError("First name is required");

      return false;
    }

    if (!formData.last_name.trim() && !isNew) {
      setError("Last name is required");

      return false;
    }

    if (!formData.role_id && !isNew) {
      setError("Role is required");

      return false;
    }

    return true;
  };

  const validateNewUserForm = () => {
    if (!newUserData.username.trim()) {
      setCreateError("Username is required");

      return false;
    }

    if (newUserData.email && !/\S+@\S+\.\S+/.test(newUserData.email)) {
      setCreateError("Invalid email format");

      return false;
    }

    if (!newUserData.first_name.trim()) {
      setCreateError("First name is required");

      return false;
    }

    if (!newUserData.last_name.trim()) {
      setCreateError("Last name is required");

      return false;
    }

    if (!newUserData.role_id) {
      setCreateError("Role is required");

      return false;
    }

    if (!newUserData.password.trim()) {
      setCreateError("Password is required");

      return false;
    }

    if (newUserData.password.length < 8) {
      setCreateError("Password must be at least 8 characters");

      return false;
    }

    if (newUserData.password !== newUserData.confirm_password) {
      setCreateError("Passwords do not match");

      return false;
    }

    return true;
  };

  const validatePasswordForm = () => {
    if (!passwordData.password.trim()) {
      setPasswordError("Password is required");

      return false;
    }

    if (passwordData.password.length < 8) {
      setPasswordError("Password must be at least 8 characters");

      return false;
    }

    if (passwordData.password !== passwordData.confirm_password) {
      setPasswordError("Passwords do not match");

      return false;
    }

    return true;
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !validateUserForm()) return;

    try {
      setUpdating(true);
      setError("");

      await usersService.updateUser(selectedUser.id, {
        username: formData.username,
        email: formData.email || null,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        role_id: formData.role_id,
        is_active: formData.is_active,
      });

      onEditClose();
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateNewUser = async () => {
    if (!validateNewUserForm()) return;

    try {
      setCreating(true);
      setCreateError("");

      await usersService.createUser({
        username: newUserData.username,
        email: newUserData.email || null,
        first_name: newUserData.first_name,
        last_name: newUserData.last_name,
        phone: newUserData.phone || null,
        role_id: newUserData.role_id,
        password: newUserData.password,
      });

      onCreateClose();
      onUserCreated();
    } catch (err) {
      console.error("Error creating user:", err);
      setCreateError("Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!validatePasswordForm()) return;

    try {
      setChangingPassword(true);
      setPasswordError("");

      await usersService.updateUserPassword(
        passwordData.user_id,
        passwordData.password,
      );

      onPasswordClose();
    } catch (err) {
      console.error("Error updating password:", err);
      setPasswordError("Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await usersService.updateUserStatus(userId, false);
      onUserDeleted(userId);
    } catch (err) {
      console.error("Error deleting user:", err);
    }
  };

  const getRoleName = (roleId: number) => {
    const role = roles.find((r) => r.id === roleId);

    return role ? role.name : "Unknown";
  };

  const renderCell = (user: User, columnKey: React.Key) => {
    switch (columnKey) {
      case "username":
        return (
          <div className="flex flex-col">
            <p className="font-medium">{user.username}</p>
            {user.email && (
              <p className="text-small text-default-500">{user.email}</p>
            )}
          </div>
        );
      case "name":
        return (
          <div className="flex flex-col">
            <p className="font-medium">
              {user.first_name} {user.last_name}
            </p>
            {user.phone && (
              <p className="text-small text-default-500">{user.phone}</p>
            )}
          </div>
        );
      case "role":
        return (
          <Chip size="sm" variant="flat">
            {user.role_name || getRoleName(user.role_id)}
          </Chip>
        );
      case "status":
        return (
          <Chip
            color={user.is_active ? "success" : "danger"}
            size="sm"
            variant="flat"
          >
            {user.is_active ? "Active" : "Inactive"}
          </Chip>
        );
      case "last_login":
        return user.last_login ? formatDate(user.last_login) : "Never";
      case "actions":
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleViewUser(user)}
            >
              <Eye size={16} />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleEditUser(user)}
            >
              <Pencil size={16} />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handlePasswordChange(user.id)}
            >
              <Lock size={16} />
            </Button>
            {user.is_active && (
              <Popover>
                <PopoverTrigger>
                  <Button isIconOnly color="danger" size="sm" variant="light">
                    <Trash2 size={16} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="px-4 py-3 space-y-4">
                    <div className="text-small font-bold">
                      Are you sure you want to deactivate this user?
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        color="danger"
                        variant="light"
                        onPress={() => handleDeleteUser(user.id)}
                      >
                        Deactivate
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        );
      default:
        return user[columnKey as keyof User];
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Users</h2>
        <Button
          color="primary"
          startContent={<UserPlus size={16} />}
          onPress={handleCreateUser}
        >
          Add User
        </Button>
      </div>

      <div className="max-w-full overflow-x-auto">
        <Table
          aria-label="Users table"
          bottomContent={
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-small text-default-400">
                  Rows per page:
                </span>
                <select
                  className="bg-transparent text-small text-default-400 border border-default-400 rounded-md px-2 py-1"
                  value={pagination.pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <Pagination
                showControls
                color="primary"
                page={pagination.page}
                total={Math.ceil(pagination.total / pagination.pageSize)}
                onChange={onPageChange}
              />
            </div>
          }
          classNames={{
            wrapper: "min-h-[222px]",
          }}
        >
          <TableHeader>
            <TableColumn key="username">USERNAME / EMAIL</TableColumn>
            <TableColumn key="name">NAME / PHONE</TableColumn>
            <TableColumn key="role">ROLE</TableColumn>
            <TableColumn key="status">STATUS</TableColumn>
            <TableColumn key="last_login">LAST LOGIN</TableColumn>
            <TableColumn key="actions" className="text-end">
              ACTIONS
            </TableColumn>
          </TableHeader>
          <TableBody
            emptyContent={
              <div className="py-8 text-center">
                <p className="text-default-500">No users found</p>
              </div>
            }
            items={users}
            loadingContent={<Spinner />}
            loadingState={loading ? "loading" : "idle"}
          >
            {(user) => (
              <TableRow key={user.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(user, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* View/Edit User Modal */}
      <Modal isOpen={isEditOpen} size="lg" onClose={onEditClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>
                {viewMode ? "User Details" : "Edit User"}
              </ModalHeader>
              <ModalBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    isReadOnly={viewMode}
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                  />
                  <Input
                    isReadOnly={viewMode}
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  <Input
                    isReadOnly={viewMode}
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                  />
                  <Input
                    isReadOnly={viewMode}
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                  />
                  <Input
                    isReadOnly={viewMode}
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                  <Select
                    isDisabled={viewMode}
                    label="Role"
                    name="role_id"
                    selectedKeys={[formData.role_id.toString()]}
                    onChange={handleInputChange}
                  >
                    {roles.map((role) => (
                      <SelectItem key={role.id} textValue={role.name}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <Switch
                    isDisabled={viewMode}
                    isSelected={formData.is_active}
                    onValueChange={handleStatusChange}
                  />
                  <span>Active</span>
                </div>

                {selectedUser && viewMode && (
                  <div className="space-y-2 mt-4">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Created At:</span>
                      <span>{formatDate(selectedUser.created_at)}</span>
                    </div>
                    {selectedUser.last_login && (
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">Last Login:</span>
                        <span>{formatDate(selectedUser.last_login)}</span>
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <div className="text-danger text-sm mt-4">{error}</div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  {viewMode ? "Close" : "Cancel"}
                </Button>
                {!viewMode && (
                  <Button
                    color="primary"
                    isLoading={updating}
                    onPress={handleUpdateUser}
                  >
                    Update
                  </Button>
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Create User Modal */}
      <Modal isOpen={isCreateOpen} size="lg" onClose={onCreateClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Create New User</ModalHeader>
              <ModalBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    isRequired
                    label="Username"
                    name="username"
                    value={newUserData.username}
                    onChange={handleNewUserInputChange}
                  />
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={newUserData.email}
                    onChange={handleNewUserInputChange}
                  />
                  <Input
                    isRequired
                    label="First Name"
                    name="first_name"
                    value={newUserData.first_name}
                    onChange={handleNewUserInputChange}
                  />
                  <Input
                    isRequired
                    label="Last Name"
                    name="last_name"
                    value={newUserData.last_name}
                    onChange={handleNewUserInputChange}
                  />
                  <Input
                    label="Phone"
                    name="phone"
                    value={newUserData.phone}
                    onChange={handleNewUserInputChange}
                  />
                  <Select
                    isRequired
                    label="Role"
                    name="role_id"
                    selectedKeys={
                      newUserData.role_id
                        ? [newUserData.role_id.toString()]
                        : []
                    }
                    onChange={handleNewUserInputChange}
                  >
                    {roles.map((role) => (
                      <SelectItem key={role.id} textValue={role.name}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </Select>
                  <Input
                    isRequired
                    label="Password"
                    name="password"
                    type="password"
                    value={newUserData.password}
                    onChange={handleNewUserInputChange}
                  />
                  <Input
                    isRequired
                    label="Confirm Password"
                    name="confirm_password"
                    type="password"
                    value={newUserData.confirm_password}
                    onChange={handleNewUserInputChange}
                  />
                </div>

                {createError && (
                  <div className="text-danger text-sm mt-4">{createError}</div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  isLoading={creating}
                  onPress={handleCreateNewUser}
                >
                  Create
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Change Password Modal */}
      <Modal isOpen={isPasswordOpen} onClose={onPasswordClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Change Password</ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Input
                    isRequired
                    label="New Password"
                    name="password"
                    type="password"
                    value={passwordData.password}
                    onChange={handlePasswordInputChange}
                  />
                  <Input
                    isRequired
                    label="Confirm Password"
                    name="confirm_password"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordInputChange}
                  />
                </div>

                {passwordError && (
                  <div className="text-danger text-sm mt-4">
                    {passwordError}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  isLoading={changingPassword}
                  onPress={handleUpdatePassword}
                >
                  Update Password
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
